import RecommendationsService from "@reco/service/src/";
import { Scenario } from "@reco/shared/src/constants";
import { ABTestT82 } from "@reco/shared/src/types/abTest";
import type { RecommendationsArgs } from "@reco/shared/src/types/recommendation";
import {
  createWidgetOptions,
  getErpNumber,
  getMountElements,
  mountWidgetBasedOnScenario,
  shouldCheckOSCountry,
} from "@reco/widgets/src/utils";

import { NewPageType } from "@reco/service/src/utils/pageType";
import { getContextId } from "@reco/service/src/utils/params";
import { mountPageRecommendations } from "./batchMount";
import { useFeatureToggle } from "./composables/useFeatureToggle";
import enableMocking from "./mocks/enableMocking";
import { sharedPinia } from "./stores";
import { usePageStore } from "./stores/pageStore";
import "./style.scss";

const NON_BATCH_SCENARIOS = [
  Scenario.BUNDLES,
  Scenario.SHOPTHELOOK,
  Scenario.COMPARISONS,
  Scenario.SPONSORED,
  Scenario.CARTOVERLAY,
];

export const mountRecoWidgets = async () => {
  const { locale, country, language, pageType, newPageType } = usePageStore(sharedPinia);
  const { isFeatureEnabled } = useFeatureToggle(country);

  if (shouldCheckOSCountry() && !isFeatureEnabled("recommendations")) {
    console.warn(
      `[Reco Widgets] Current domain ${window.location.hostname} is not an OS country. Not mounting RECO widgets.`,
    );
    return;
  }

  const isBatchEnabled = isFeatureEnabled("dynamicPdpRecos");

  const elements = getMountElements();
  if (!elements.length) return;

  const service = new RecommendationsService();
  const erpNumber = getErpNumber();

  const mountLegacyWidget = async (elem: HTMLElement) => {
    try {
      const recoArgs = { ...elem.dataset } as unknown as RecommendationsArgs;
      // NEOSRF-180: Add reco class to all recommendation elements to prevent CSS conflicts (see vite.config.ts)
      elem.classList.add("reco");

      if (erpNumber) {
        recoArgs.contextId = erpNumber;
      }

      // PRECO-3750: Temporarily change cartoverlay slider to cross when AB Test is active
      const isCrossCartoverlay = recoArgs.type === Scenario.CARTOVERLAY && window.t82 === ABTestT82.A1_CROSSSELLING;

      if (isCrossCartoverlay) {
        recoArgs.type = Scenario.CROSS;
      }

      if (recoArgs.type === Scenario.COMPARISONS) {
        console.info(`[Reco Widgets]: Comparison scenario is disabled`);
        return;
      }

      const response = await service.getRecommendations(recoArgs);
      const compOptions = createWidgetOptions(response, elem, { locale, country, language });

      // PRECO-3750: Temporarily tell the recos that they are part of the cartoverlay when AB Test is active
      if ("recommendations" in compOptions && compOptions.recommendations && isCrossCartoverlay) {
        compOptions.recommendations.forEach((recommendation) => (recommendation.list = "cartoverlay"));
      }

      mountWidgetBasedOnScenario(response, elem, compOptions);
    } catch (err) {
      console.error(err);
    }
  };

  if (isBatchEnabled && newPageType === NewPageType.PRODUCT_DETAIL_PAGE) {
    const batchElements: HTMLElement[] = [];

    elements.forEach((elem) => {
      elem.classList.add("reco");
      const scenario = elem.dataset.type as Scenario;

      if (NON_BATCH_SCENARIOS.includes(scenario)) {
        mountLegacyWidget(elem);
      } else if (scenario !== Scenario.COMPARISONS) {
        batchElements.push(elem);
      }
    });

    if (batchElements.length) {
      try {
        const success = await mountPageRecommendations(
          batchElements,
          { locale, country, language, pageType: newPageType },
          getContextId(pageType, window.location.pathname),
        );

        if (!success) {
          console.warn("[Reco Widgets]: Batch recommendations empty, falling back to legacy widgets.");
          batchElements.forEach((elem) => mountLegacyWidget(elem));
        }
      } catch (error) {
        console.error("[Reco Widgets]: Error during batch mounting, falling back to legacy widgets:", error);
        batchElements.forEach((elem) => mountLegacyWidget(elem));
      }
    }
  } else {
    elements.forEach((elem) => mountLegacyWidget(elem));
  }
};

if (!import.meta.env.STORYBOOK) {
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      enableMocking().then(() => {
        mountRecoWidgets();
      });
    },
    { once: true },
  );

  window.addEventListener("recommendations-slider-show", mountRecoWidgets);
}
